// ============================================================================
// CLEO'S SECRET — ClientPortal
// File: src/pages/ClientPortal.jsx
//
// The patient hub. Foundational DASHBOARD pattern (different from editorial
// pages) — this is the template MyResults / CleoScorePage / RetentionPage
// will follow.
//
// Key differences from editorial pages:
//   • Tighter padding (40-60px section gaps, not 100+)
//   • Page-header strip at top (small eyebrow + greeting + email), then
//     main content container with sections
//   • Compact SectionHead (Cinzel label + count + horizontal rule)
//     instead of full SectionDivider+SectionHeadline+body intro
//   • Action cards / appointment cards / record cards in a single column
//   • OBS Cleo Score hero card for the primary metric
//
// Three states: unauthenticated → loading → authenticated dashboard
// Three API calls preserved: get-patient-data, get-patient-results, get-cleo-scores
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  FF, FS, P, T,
  Eyebrow,
  BtnPrimary,
  Spinner,
} from '../lib/design-tokens';
import Ankh from '../components/Ankh';
import GraceCancelTrigger from '../components/GraceCancelTrigger';

// ───────── Date formatters ─────────
function formatDate(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}
function formatDateShort(s) {
  if (!s) return '';
  return new Date(s).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

// ───────── Score → status color ─────────
function scoreColor(score) {
  if (score >= 80) return P.GREEN;
  if (score >= 65) return P.GO;
  if (score >= 50) return P.AMBER;
  return P.RED;
}

// ───────── ScoreRing — radial progress ─────────
function ScoreRing(props) {
  var size = props.size || 100;
  var stroke = props.stroke || 7;
  var score = props.score || 0;
  var color = props.color || P.GO;
  var onDark = props.onDark;
  var r = (size - stroke) / 2;
  var c = 2 * Math.PI * r;
  var offset = c - (score / 100) * c;
  var trackColor = onDark ? 'rgba(224,198,133,0.2)' : 'rgba(201,169,97,0.2)';
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }} aria-hidden="true">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
              strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
              style={{ transition: 'stroke-dashoffset 1s ease-out' }} />
    </svg>
  );
}

// ───────── SectionHead — Cinzel label + optional count + rule ─────────
function SectionHead({ label, count, action }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'baseline',
      justifyContent: 'space-between',
      gap: 16,
      marginBottom: 'clamp(20px, 2.2vw, 28px)',
      paddingBottom: 'clamp(12px, 1.4vw, 16px)',
      borderBottom: '1px solid rgba(154,116,48,0.22)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ ...T.label, color: P.BR2, margin: 0 }}>{label}</h2>
        {(count !== undefined && count !== null) && (
          <span style={{
            fontFamily: FF,
            fontSize: 'clamp(12px, 1vw, 14px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            color: P.TL,
          }}>{String(count).padStart(2, '0')}</span>
        )}
      </div>
      {action}
    </div>
  );
}

// ───────── EmptyState — for empty sections ─────────
function EmptyState({ children }) {
  return (
    <div style={{
      padding: 'clamp(20px, 2.4vw, 32px)',
      background: 'rgba(250, 246, 238, 0.55)',
      border: '1px dashed ' + P.SAND3,
      textAlign: 'center',
      ...T.bodyMd,
      fontStyle: 'italic',
      color: P.TF,
    }}>{children}</div>
  );
}

// ───────── ActionCard ─────────
function ActionCard({ icon, title, subtitle, badge, highlight, onClick }) {
  var onHover = function(e) {
    e.currentTarget.style.borderColor = P.GO;
    e.currentTarget.style.transform = 'translateY(-1px)';
  };
  var onLeave = function(e) {
    e.currentTarget.style.borderColor = highlight ? P.GO : P.SAND3;
    e.currentTarget.style.transform = 'translateY(0)';
  };
  return (
    <div
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(16px, 1.8vw, 24px)',
        padding: 'clamp(18px, 2vw, 26px) clamp(18px, 2vw, 28px)',
        background: P.PAP,
        border: '1px solid ' + (highlight ? P.GO : P.SAND3),
        cursor: 'pointer',
        marginBottom: 10,
        transition: 'border-color 0.2s, transform 0.2s',
        position: 'relative',
      }}>
      {/* Icon badge */}
      <div style={{
        width: 'clamp(38px, 3.6vw, 44px)',
        height: 'clamp(38px, 3.6vw, 44px)',
        background: 'rgba(201,169,97,0.08)',
        border: '1px solid rgba(201,169,97,0.32)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        color: P.BR,
      }}>{icon}</div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(16px, 1.4vw, 19px)',
          fontWeight: 500,
          color: P.INK,
          marginBottom: 4,
          letterSpacing: '-0.003em',
        }}>{title}</div>
        <div style={{
          ...T.bodyMd,
          fontSize: 'clamp(13px, 1.1vw, 15px)',
          color: P.TF,
        }}>{subtitle}</div>
      </div>

      {/* Optional badge */}
      {badge && (
        <span style={{
          fontFamily: FF,
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          background: P.GO,
          color: P.INK,
          padding: '4px 10px',
        }}>{badge}</span>
      )}

      {/* Arrow */}
      <span style={{
        fontSize: 22,
        color: P.GO,
        lineHeight: 1,
        flexShrink: 0,
      }} aria-hidden="true">→</span>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ClientPortal
// ════════════════════════════════════════════════════════════════════════════
export default function ClientPortal(props) {
  var onNavigate = props.onNavigate;
  var user = props.user;
  var email = props.email;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var resultsCountState = useState({ approved: 0, pending: 0 });
  var resultsCount = resultsCountState[0];
  var setResultsCount = resultsCountState[1];

  var cleoScoreState = useState(null);
  var cleoScore = cleoScoreState[0];
  var setCleoScore = cleoScoreState[1];

  var activePlanState = useState(null);
  var activePlan = activePlanState[0];
  var setActivePlan = activePlanState[1];

  var programPlanSnapshotState = useState(null);
  var programPlanSnapshot = programPlanSnapshotState[0];
  var setProgramPlanSnapshot = programPlanSnapshotState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  useEffect(function() {
    if (!email) { setLoading(false); return; }

    fetch('/api/get-patient-data', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) {
        if (!res.ok) {
          // 404 in dev typically means /api routes aren't being served
          // (CRA dev server alone doesn't host Vercel functions — run `vercel dev` for full stack).
          if (res.status === 404) throw new Error('API unreachable. Is the dev server running with vercel dev?');
          throw new Error('Could not load your data.');
        }
        return res.json();
      })
      .then(function(result) { setData(result); setLoading(false); })
      .catch(function(err) { setError(err.message); setLoading(false); });

    fetch('/api/get-patient-results', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        setResultsCount({
          approved: (result.results || []).length,
          pending: (result.pending || []).length,
        });
      })
      .catch(function() {});

    fetch('/api/get-cleo-scores', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) { setCleoScore(result.latest); })
      .catch(function() {});

    // Within-grace plan check — drives the conditional grace-cancel card.
    // Fail-soft: if the endpoint is unavailable (e.g. dev server without
    // vercel functions), the card simply doesn't render.
    fetch('/api/program-active-plan', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) { setActivePlan(result && result.plan ? result.plan : null); })
      .catch(function() {});

    // Program state snapshot — drives the "Continue your program" ActionCard.
    // Fail-soft: silent ignore on error (no ActionCard renders if endpoint fails).
    fetch('/api/get-program-state', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email })
    })
      .then(function(res) { return res.json(); })
      .then(function(result) {
        if (result && result.plan) {
          setProgramPlanSnapshot({
            status: result.plan.status,
            currentWeek: result.currentWeek,
            completed: result.completed,
          });
        }
      })
      .catch(function() {});
  }, [email]);

  // ═════════════════════════════════════════════════════════════════════
  // UNAUTHENTICATED — sign-in prompt
  // ═════════════════════════════════════════════════════════════════════
  if (!email && !user) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.SAND,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'clamp(40px, 6vw, 80px) clamp(20px, 4vw, 48px)',
      }}>
        <div style={{
          maxWidth: 600,
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}>
          <div style={{ marginBottom: 'clamp(24px, 2.5vw, 36px)', display: 'flex', justifyContent: 'center' }}>
            <Ankh responsive="opener" color={P.GO} />
          </div>
          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(36px, 4.8vw, 60px)',
            fontWeight: 400,
            lineHeight: 1.08,
            letterSpacing: '-0.018em',
            color: P.INK,
            margin: '0 auto 32px',
            maxWidth: 600,
          }}>
            <span style={{ display: 'block' }}>Sign in</span>
            <span style={{ display: 'block', fontStyle: 'italic', color: P.BR2, fontSize: '0.9em' }}>to your portal.</span>
          </h1>
          <div style={{
            width: 'clamp(140px, 18vw, 220px)',
            height: 2,
            background: P.GO,
            opacity: 0.9,
            margin: '0 auto clamp(28px, 3vw, 40px)',
          }} />
          <p style={{
            ...T.bodyLg,
            color: P.TX,
            maxWidth: 480,
            margin: '0 auto clamp(36px, 4vw, 48px)',
          }}>
            Access your Cleo Score, bookings, results, prep packs, and your full conversation history with the clinic.
          </p>
          <BtnPrimary onClick={function() { if (onNavigate) onNavigate('login'); }} large>
            Sign in
          </BtnPrimary>
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // LOADING
  // ═════════════════════════════════════════════════════════════════════
  if (loading) {
    return (
      <div style={{
        fontFamily: FF,
        background: P.SAND,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════
  // AUTHENTICATED DASHBOARD
  // ═════════════════════════════════════════════════════════════════════
  var patient = data && data.patient;
  var bookings = (data && data.bookings) || [];
  var intakes = (data && data.intakes) || [];
  var prepPacks = (data && data.prepPacks) || [];

  var today = new Date();
  today.setHours(0, 0, 0, 0);
  var upcoming = bookings.filter(function(b) { return new Date(b.appointment_date) >= today; });
  var past = bookings.filter(function(b) { return new Date(b.appointment_date) < today; });

  var firstName = patient && patient.first_name;

  // Icon set — kept as simple outline SVGs
  var iconCleoScore = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <circle cx="9" cy="9.5" r="0.5" fill="currentColor" />
      <circle cx="15" cy="9.5" r="0.5" fill="currentColor" />
    </svg>
  );
  var iconResults = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="9" y1="15" x2="15" y2="15" />
      <line x1="9" y1="11" x2="15" y2="11" />
    </svg>
  );
  var iconUpload = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
  var iconCalendar = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="0" ry="0" />
      <line x1="16" y1="2" x2="16" y2="6" />
      <line x1="8" y1="2" x2="8" y2="6" />
      <line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  );
  var iconProgram = (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="0" ry="0" />
      <line x1="3" y1="10" x2="21" y2="10" />
      <path d="M12 14 a4 4 0 0 1 4 4" />
    </svg>
  );

  return (
    <div style={{ fontFamily: FF, background: P.SAND, minHeight: '100vh' }}>

      {/* ═════════ Header strip (SAND) ═════════ */}
      <header style={{
        background: P.SAND,
        padding: 'clamp(28px, 3.5vw, 48px) clamp(20px, 3vw, 40px)',
        textAlign: 'center',
        borderBottom: '1px solid ' + P.SAND3,
      }}>
        <div style={{ marginBottom: 16 }}>
          <Eyebrow center>Your portal</Eyebrow>
        </div>
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(28px, 3.4vw, 44px)',
          fontWeight: 400,
          lineHeight: 1.12,
          letterSpacing: '-0.012em',
          color: P.INK,
          margin: 0,
        }}>
          {firstName
            ? <>Welcome back, <span style={{ fontStyle: 'italic', color: P.BR2 }}>{firstName}</span>.</>
            : 'Welcome back.'}
        </h1>
        {email && (
          <div style={{
            ...T.bodySm,
            color: P.TF,
            marginTop: 10,
            letterSpacing: '0.04em',
          }}>{email}</div>
        )}
      </header>

      {/* ═════════ Main content ═════════ */}
      <main style={{
        maxWidth: 920,
        margin: '0 auto',
        padding: 'clamp(32px, 4vw, 56px) clamp(20px, 3vw, 40px) clamp(64px, 8vw, 120px)',
      }}>

        {/* Error banner (if any) */}
        {error && (
          <div style={{
            padding: 'clamp(16px, 1.8vw, 22px) clamp(20px, 2.2vw, 28px)',
            background: P.RED_BG,
            border: '1px solid ' + P.RED,
            borderLeft: '3px solid ' + P.RED,
            marginBottom: 'clamp(28px, 3vw, 40px)',
            ...T.bodyMd,
            fontStyle: 'italic',
            color: P.RED,
          }}>{error}</div>
        )}

        {/* Within-grace cancellation card — only renders when api/program-active-plan
            returns a plan (enrolled within the last 48h). Hides itself after
            successful cancel via internal state. */}
        {activePlan && (
          <GraceCancelTrigger
            variant="card"
            plan={activePlan}
            email={email}
            onCancelled={function() { setActivePlan(null); }}
          />
        )}

        {/* ═════ Cleo Score hero (dark) ═════ */}
        {cleoScore && (
          <div
            onClick={function() { if (onNavigate) onNavigate('cleo-score'); }}
            onMouseEnter={function(e) { e.currentTarget.style.borderColor = P.GO2; }}
            onMouseLeave={function(e) { e.currentTarget.style.borderColor = P.GO; }}
            style={{
              background: P.OBS,
              color: P.IVY,
              border: '1px solid ' + P.GO,
              padding: 'clamp(28px, 3.4vw, 48px)',
              marginBottom: 'clamp(36px, 4vw, 56px)',
              display: 'flex',
              alignItems: 'center',
              gap: 'clamp(24px, 3vw, 40px)',
              cursor: 'pointer',
              transition: 'border-color 0.25s',
            }}>
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <ScoreRing
                size={100}
                stroke={7}
                score={cleoScore.overall_score}
                color={scoreColor(cleoScore.overall_score)}
                onDark
              />
              <div style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{
                  fontFamily: FF,
                  fontSize: 'clamp(30px, 3vw, 40px)',
                  fontWeight: 400,
                  color: P.IVY,
                  lineHeight: 1,
                  letterSpacing: '-0.015em',
                }}>{cleoScore.overall_score}</span>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                ...T.label,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                color: P.GO2,
                marginBottom: 10,
              }}>Your Cleo Score</div>
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(20px, 2vw, 28px)',
                fontWeight: 400,
                color: P.IVY,
                lineHeight: 1.25,
                marginBottom: 8,
                letterSpacing: '-0.005em',
              }}>{cleoScore.pattern}</div>
              <div style={{
                fontFamily: FS,
                fontSize: 'clamp(12px, 0.95vw, 13px)',
                fontWeight: 600,
                color: 'rgba(240,232,212,0.6)',
                letterSpacing: '0.04em',
              }}>Tap to view full breakdown</div>
            </div>
            <span style={{
              fontSize: 24,
              color: P.GO2,
              flexShrink: 0,
              lineHeight: 1,
            }} aria-hidden="true">→</span>
          </div>
        )}

        {/* ═════ Quick actions ═════ */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
          <SectionHead label="Quick actions" />

          {programPlanSnapshot && (
            <ActionCard
              icon={iconProgram}
              title={programPlanSnapshot.completed
                ? 'Your graduated program'
                : 'Continue your program'}
              subtitle={programPlanSnapshot.completed
                ? 'Review your weeks and graduation score'
                : 'Week ' + programPlanSnapshot.currentWeek + ' of 12'}
              highlight={!programPlanSnapshot.completed}
              onClick={function() { if (onNavigate) onNavigate('program'); }}
            />
          )}

          {!cleoScore && (
            <ActionCard
              icon={iconCleoScore}
              title="Generate your Cleo Score"
              subtitle="A personalised health metric across four dimensions"
              highlight={true}
              onClick={function() { if (onNavigate) onNavigate('cleo-score'); }}
            />
          )}

          {(resultsCount.approved > 0 || resultsCount.pending > 0) && (
            <ActionCard
              icon={iconResults}
              title="My results"
              subtitle={resultsCount.pending > 0
                ? resultsCount.pending + ' being prepared by your GP'
                : resultsCount.approved + ' result' + (resultsCount.approved === 1 ? '' : 's') + ' ready to read'}
              highlight={resultsCount.approved > 0}
              badge={resultsCount.approved > 0 ? 'New' : null}
              onClick={function() { if (onNavigate) onNavigate('my-results'); }}
            />
          )}

          <ActionCard
            icon={iconUpload}
            title="Upload pathology results"
            subtitle="Get an AI-prepared interpretation reviewed by your GP"
            onClick={function() { if (onNavigate) onNavigate('pathology-upload'); }}
          />

          <ActionCard
            icon={iconCalendar}
            title="Book a consultation"
            subtitle="Schedule your next appointment with your GP"
            onClick={function() { if (onNavigate) onNavigate('scheduler'); }}
          />
        </section>

        {/* ═════ Upcoming appointments ═════ */}
        <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
          <SectionHead label="Upcoming appointments" count={upcoming.length || null} />

          {upcoming.length === 0 ? (
            <EmptyState>You have no upcoming appointments.</EmptyState>
          ) : (
            upcoming.map(function(b) {
              return (
                <div key={b.id} style={{
                  padding: 'clamp(20px, 2.2vw, 28px)',
                  marginBottom: 10,
                  background: 'rgba(201,169,97,0.06)',
                  border: '1px solid ' + P.GO,
                  borderLeft: '3px solid ' + P.GO,
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginBottom: 8,
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(16px, 1.4vw, 19px)',
                      fontWeight: 500,
                      color: P.INK,
                      letterSpacing: '-0.003em',
                    }}>{b.consultation_type}</div>
                    <div style={{
                      ...T.label,
                      fontSize: 'clamp(10px, 0.85vw, 12px)',
                      letterSpacing: '0.24em',
                      color: P.BR2,
                      flexShrink: 0,
                    }}>{b.status}</div>
                  </div>
                  <div style={{
                    ...T.bodyMd,
                    color: P.TX,
                    marginBottom: 4,
                  }}>{b.practitioner_name}</div>
                  <div style={{
                    ...T.bodyMd,
                    color: P.TL,
                    marginBottom: 6,
                  }}>{formatDate(b.appointment_date)} at {b.appointment_time}</div>
                  <div style={{
                    ...T.bodySm,
                    color: P.TF,
                    letterSpacing: '0.04em',
                  }}>{b.duration_minutes} minutes &middot; ${b.fee_amount}</div>
                </div>
              );
            })
          )}
        </section>

        {/* ═════ Prep packs ═════ */}
        {prepPacks.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Your prep packs" count={prepPacks.length} />
            {prepPacks.map(function(p) {
              var greeting = p.content && p.content.greeting
                ? p.content.greeting.substring(0, 100).trim() + '…'
                : 'Personalised preparation document';
              return (
                <div key={p.id} style={{
                  padding: 'clamp(18px, 2vw, 24px)',
                  marginBottom: 10,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 12,
                    marginBottom: 8,
                  }}>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(16px, 1.3vw, 18px)',
                      fontWeight: 500,
                      color: P.INK,
                    }}>Cleo Prep Pack</div>
                    <div style={{
                      ...T.bodySm,
                      color: P.TF,
                      letterSpacing: '0.04em',
                      flexShrink: 0,
                    }}>{formatDateShort(p.created_at)}</div>
                  </div>
                  <div style={{
                    ...T.bodyMd,
                    color: P.TL,
                    fontStyle: 'italic',
                  }}>{greeting}</div>
                </div>
              );
            })}
          </section>
        )}

        {/* ═════ Past consultations ═════ */}
        {past.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Past consultations" count={past.length} />
            {past.map(function(b) {
              return (
                <div key={b.id} style={{
                  padding: 'clamp(14px, 1.6vw, 20px) clamp(16px, 1.8vw, 22px)',
                  marginBottom: 8,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline',
                  gap: 12,
                  flexWrap: 'wrap',
                }}>
                  <div>
                    <div style={{
                      fontFamily: FF,
                      fontSize: 'clamp(15px, 1.2vw, 17px)',
                      fontWeight: 500,
                      color: P.INK,
                      marginBottom: 2,
                    }}>{b.consultation_type}</div>
                    <div style={{
                      ...T.bodySm,
                      color: P.TF,
                    }}>{b.practitioner_name}</div>
                  </div>
                  <div style={{
                    ...T.bodySm,
                    color: P.TL,
                    letterSpacing: '0.04em',
                  }}>{formatDateShort(b.appointment_date)}</div>
                </div>
              );
            })}
          </section>
        )}

        {/* ═════ Conversation history ═════ */}
        {intakes.length > 0 && (
          <section style={{ marginBottom: 'clamp(40px, 4.5vw, 64px)' }}>
            <SectionHead label="Conversation history" count={intakes.length} />
            {intakes.map(function(i) {
              var concern = i.clinical_summary && i.clinical_summary.primary_concern
                ? i.clinical_summary.primary_concern
                : 'Health intake conversation';
              return (
                <div key={i.id} style={{
                  padding: 'clamp(14px, 1.6vw, 20px) clamp(16px, 1.8vw, 22px)',
                  marginBottom: 8,
                  background: P.PAP,
                  border: '1px solid ' + P.SAND3,
                }}>
                  <div style={{
                    fontFamily: FF,
                    fontSize: 'clamp(15px, 1.2vw, 17px)',
                    fontWeight: 500,
                    color: P.INK,
                    marginBottom: 6,
                    lineHeight: 1.4,
                  }}>{concern}</div>
                  <div style={{
                    ...T.bodySm,
                    color: P.TF,
                    letterSpacing: '0.04em',
                  }}>{formatDateShort(i.completed_at || i.created_at)}</div>
                </div>
              );
            })}
          </section>
        )}

        {/* ═════ Sign out ═════ */}
        {user && (
          <div style={{
            textAlign: 'center',
            marginTop: 'clamp(40px, 4.5vw, 56px)',
            paddingTop: 'clamp(28px, 3vw, 40px)',
            borderTop: '1px solid ' + P.SAND3,
          }}>
            <button
              onClick={function() { if (onNavigate) onNavigate('signout'); }}
              style={{
                fontFamily: FS,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 600,
                letterSpacing: '0.28em',
                textTransform: 'uppercase',
                padding: '12px 28px',
                background: 'transparent',
                color: P.TF,
                border: '1px solid ' + P.SAND3,
                cursor: 'pointer',
                transition: 'all 0.2s',
              }}
              onMouseEnter={function(e) {
                e.currentTarget.style.color = P.BR;
                e.currentTarget.style.borderColor = P.GO;
              }}
              onMouseLeave={function(e) {
                e.currentTarget.style.color = P.TF;
                e.currentTarget.style.borderColor = P.SAND3;
              }}>
              Sign out
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
